import { CheckoutSessionPayload, UpgradeAgreementPayload } from "@/schema/onboarding"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  claimAccount,
  completeOnboarding,
  createCheckoutSession,
  createUpgradeAgreement,
  CrmMigrationRequestPayload,
  setPendingPageportUpdate,
  submitCrmMigrationRequest,
} from "./api"

export function useCreateCheckoutSession() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (data: CheckoutSessionPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return createCheckoutSession(context, data)
    },
  })
}

export function useClaimAccount() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return claimAccount(context)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] })
      queryClient.invalidateQueries({ queryKey: ["onboarding"] })
    },
  })
}

export function useCreateUpgradeAgreement() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (data: UpgradeAgreementPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return createUpgradeAgreement(context, data)
    },
  })
}

export function useCompleteOnboarding() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return completeOnboarding(context)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding"] })
    },
  })
}

export function useSubmitCrmMigrationRequest() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (data: CrmMigrationRequestPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return submitCrmMigrationRequest(context, data)
    },
  })
}

export function useSetPendingPageportUpdate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return setPendingPageportUpdate(context)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["onboarding"], data)
    },
  })
}
